import React, { useId, useRef, useState } from 'react';
import { renderSkuWithSoftBreaks } from '../../../utils/skuSoftBreak';
import ProductImage from '../../shared/ProductImage';
import {
  isValidSplitQuantity,
  linkSplitQuantity,
  type LinkedSplitQuantity,
  type SplitQuantityField,
} from './splitShipmentBoxes';

interface SplitQuantityModalProps {
  readonly sku: string;
  readonly name: string;
  readonly fromLabel: string;
  readonly toLabel: string;
  /** Units of this SKU in the held box. */
  readonly total: number;
  readonly onCancel: () => void;
  readonly onConfirm: (moveQty: number) => void;
}

interface SplitQuantityDraft {
  readonly stay: string;
  readonly move: string;
  /** Last split typed in full; a field cleared on focus does not change it. */
  readonly linked: LinkedSplitQuantity;
}

function draftFrom(linked: LinkedSplitQuantity): SplitQuantityDraft {
  return { stay: String(linked.stay), move: String(linked.move), linked };
}

/**
 * Same focus-clear pattern as `BundleWorkCompleteModal`, kept separate because these two fields
 * always sum to a fixed total instead of tracking a delta from a bin's start count.
 */
function SplitQuantityModal({
  sku,
  name,
  fromLabel,
  toLabel,
  total,
  onCancel,
  onConfirm,
}: SplitQuantityModalProps): React.ReactElement {
  const titleId = useId();
  // The modal opens mid-press; the release of the hold that opened it must not count as a backdrop tap.
  const pressStartedOnOverlayRef = useRef(false);
  const [draft, setDraft] = useState<SplitQuantityDraft>(() => draftFrom({ stay: total, move: 0 }));
  const valid = isValidSplitQuantity(draft.linked);

  const handleFocus = (field: SplitQuantityField): void => {
    setDraft((prev) => ({ ...prev, [field]: '' }));
  };

  const handleBlur = (field: SplitQuantityField): void => {
    setDraft((prev) => (prev[field] === '' ? draftFrom(prev.linked) : prev));
  };

  const handleChange = (field: SplitQuantityField, value: string): void => {
    if (!/^\d*$/.test(value)) return;
    const linked = linkSplitQuantity(total, field, value);
    setDraft((prev) => (linked ? draftFrom(linked) : { ...prev, [field]: value }));
  };

  const handleSubmit = (event: React.FormEvent): void => {
    event.preventDefault();
    if (valid) onConfirm(draft.linked.move);
  };

  const inputClass = `bundle-work-complete-modal-input split-quantity-input${valid ? '' : ' split-quantity-input-invalid'}`;

  return (
    <div
      className="pbl-modal-overlay bundle-work-complete-modal-overlay"
      onPointerDown={(event) => {
        pressStartedOnOverlayRef.current = event.target === event.currentTarget;
      }}
      onClick={(event) => {
        if (event.target === event.currentTarget && pressStartedOnOverlayRef.current) onCancel();
      }}
      role="presentation"
    >
      <div
        className="pbl-modal bundle-work-complete-modal split-quantity-modal"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-labelledby={titleId}
        aria-modal="true"
      >
        <button type="button" className="pbl-modal-close" onClick={onCancel} aria-label="Close">
          ×
        </button>
        <h3 id={titleId} className="bundle-work-complete-modal-title">Split Quantity</h3>
        <div className="split-quantity-product">
          <span className="split-quantity-image">
            <ProductImage sku={sku} alt={name || sku} />
          </span>
          <span className="split-quantity-sku">{renderSkuWithSoftBreaks(sku)}</span>
        </div>
        <form className="bundle-work-complete-modal-form" onSubmit={handleSubmit}>
          <div className="split-quantity-fields">
            {([
              { field: 'stay', label: fromLabel },
              { field: 'move', label: toLabel },
            ] as const).map(({ field, label }) => (
              <label key={field} className="split-quantity-field">
                <span className="bundle-work-complete-modal-label">{label}</span>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  autoComplete="off"
                  className={inputClass}
                  value={draft[field]}
                  aria-label={`${label} quantity`}
                  aria-invalid={!valid}
                  onFocus={() => handleFocus(field)}
                  onBlur={() => handleBlur(field)}
                  onChange={(event) => handleChange(field, event.target.value)}
                />
              </label>
            ))}
          </div>
          <div className="bundle-work-complete-modal-actions">
            <button
              type="submit"
              className="bundle-work-complete-modal-btn bundle-work-complete-modal-btn-submit"
              disabled={!valid}
            >
              Confirm
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default SplitQuantityModal;
